import { Locale } from './locales';

/**
 * UI mode for checkout
 */
export enum UIMode {
  /** Embedded mode - checkout embedded in merchant page */
  EMBEDDED = 'embedded',
}

/**
 * Type guard for UIMode
 */
export function isValidUIMode(value: string): value is UIMode {
  return Object.values(UIMode).includes(value as UIMode);
}

/**
 * Payment method types
 */
export enum PaymentMethod {
  /** Card payment (credit/debit) */
  CARD = 'card',
}

/**
 * Type guard for PaymentMethod
 */
export function isValidPaymentMethod(value: string): value is PaymentMethod {
  return Object.values(PaymentMethod).includes(value as PaymentMethod);
}

/**
 * Wallet options for digital wallets
 */
export enum WalletOption {
  /** Apple Pay */
  APPLE_PAY = 'apple_pay',
  /** Google Pay */
  GOOGLE_PAY = 'google_pay',
}

/**
 * Type guard for WalletOption
 */
export function isValidWalletOption(value: string): value is WalletOption {
  return Object.values(WalletOption).includes(value as WalletOption);
}

/**
 * Validate payment methods array
 */
export function validatePaymentMethods(methods: string[]): PaymentMethod[] {
  const validated: PaymentMethod[] = [];

  for (const method of methods) {
    if (isValidPaymentMethod(method)) {
      validated.push(method as PaymentMethod);
    } else {
      console.warn(`[PaypercutCheckout] Invalid payment method: "${method}". Skipping.`);
    }
  }

  // Default to card if no valid methods
  if (validated.length === 0) {
    console.warn('[PaypercutCheckout] No valid payment methods provided. Defaulting to "card".');
    validated.push(PaymentMethod.CARD);
  }

  return validated;
}

/**
 * Validate wallet options array
 */
export function validateWalletOptions(options: string[]): WalletOption[] {
  const validated: WalletOption[] = [];

  for (const option of options) {
    if (isValidWalletOption(option)) {
      validated.push(option as WalletOption);
    } else {
      console.warn(`[PaypercutCheckout] Invalid wallet option: "${option}". Skipping.`);
    }
  }

  return validated;
}

/**
 * Validate UI mode
 */
export function validateUIMode(mode: string | undefined): UIMode | undefined {
  if (!mode) {
    return undefined;
  }

  if (isValidUIMode(mode)) {
    return mode as UIMode;
  }

  console.warn(
    `[PaypercutCheckout] Invalid ui_mode: "${mode}". Valid options: ${Object.values(UIMode).join(', ')}`,
  );
  return undefined;
}

/**
 * Appearance variables for customising the hosted checkout visual style.
 * All fields are optional — only override what you need.
 */
export interface AppearanceVariables {
  /** Primary button / CTA background colour (hex, e.g. '#0066ff') */
  brandColor?: `#${string}`;
  /** Font family for the checkout UI (must be available in the iframe context) */
  fontFamily?: string;
  /** Merchant logo URL shown in the checkout header (https only) */
  logoUrl?: string;
  /** Border radius for checkout panels and payment method selector (e.g. '8px', '0.5rem') */
  borderRadius?: string;
}

/**
 * Appearance options for the hosted checkout UI.
 */
export interface Appearance {
  /** Layout preset */
  preset?: 'inline' | 'modal';
  /** Colour theme */
  theme?: 'light' | 'dark';
  /** Fine-grained visual variable overrides */
  variables?: AppearanceVariables;
}

/**
 * Configuration options for PaypercutCheckout
 */
export interface PaypercutCheckoutOptions {
  /** Checkout session identifier (e.g., 'CHK_12345') */
  id: string;

  /** CSS selector or HTMLElement where iframe mounts (required) */
  containerId: string | HTMLElement;

  /**
   * Optional: Custom hosted checkout URL (only available in internal builds)
   * Production builds will ignore this option for security
   */
  hostedCheckoutUrl?: string;

  /**
   * Optional: Locale for checkout UI
   * @default 'en'
   */
  locale?: Locale | string;

  /**
   * Optional: Language for checkout UI
   * @default 'en'
   */
  lang?: Locale | string;

  /**
   * Optional: UI mode for checkout
   */
  ui_mode?: UIMode | `${UIMode}`;

  /**
   * Optional: Payment methods to enable
   * @default ['card']
   */
  payment_methods?: (PaymentMethod | `${PaymentMethod}`)[];

  /**
   * Optional: Digital wallet options
   * Can include both or just one
   * Default behaviour - shows both
   */
  wallet_options?: (WalletOption | `${WalletOption}`)[] | [];

  /**
   * Optional: Show only the payment form without header/footer
   * @default false
   */
  form_only?: boolean;

  /**
   * Optional: Appearance options for theming the checkout UI
   */
  appearance?: Appearance;

  /**
   * Optional: Require form validation before wallet payment (Google Pay / Apple Pay)
   * @default false
   */
  validate_form?: boolean;
}

/**
 * Wallet type for form validation
 */
export type WalletType = 'apple_pay' | 'google_pay';

/**
 * Form validation event payload
 */
export interface FormValidationPayload {
  checkoutId: string;
  wallet: WalletType;
}

/**
 * Wallet/checkout cancellation event payload
 */
export interface CancelPayload {
  checkoutId: string;
  wallet?: WalletType;
}

/**
 * Event names that can be emitted by the checkout
 */
export type EventName =
  | 'loaded' // iframe finished loading
  | 'processing' // payment processing started
  | 'success' // payment successful
  | 'error' // payment or system error
  | 'expired' // checkout expired or already paid
  | 'resize' // checkout expired or already paid
  | 'threeds_complete' // checkout paid successfully
  | 'threeds_error' // checkout threeds error
  | 'threeds_canceled' // checkout threeds canceled
  | 'threeds_started' // checkout threeds started
  | 'cancel' // wallet/payment flow canceled by buyer
  | 'form_validation'; // wallet button clicked, merchant should validate form

/**
 * Preferred enum for SDK event names (use instead of hardcoded strings)
 */
export enum SdkEvent {
  Loaded = 'loaded',
  Processing = 'processing',
  Success = 'success',
  Error = 'error',
  Expired = 'expired',
  Resize = 'resize',
  ThreeDSComplete = 'threeds_complete',
  ThreeDSError = 'threeds_error',
  ThreeDSCanceled = 'threeds_canceled',
  ThreeDSStarted = 'threeds_started',
  Cancel = 'cancel',
  FormValidation = 'form_validation',
}

/**
 * Form validation error for failFormValidation
 */
export interface FormValidationError {
  code?: string;
  message?: string;
}

/**
 * Checkout instance interface
 */
export interface CheckoutInstance {
  /** Mount and render the iframe into the container */
  render(): void;

  /** Submit payment - sends message to hosted checkout to confirm payment */
  submit(): void;

  /** Destroy instance and cleanup all listeners */
  destroy(): void;

  /** Subscribe to events. Returns unsubscribe function */
  on(event: EventName | SdkEvent, handler: (...args: any[]) => void): () => void;

  /** Subscribe to event that auto-unsubscribes after first emission */
  once(event: EventName | SdkEvent, handler: (...args: any[]) => void): () => void;

  /** Unsubscribe from events */
  off(event: EventName | SdkEvent, handler: (...args: any[]) => void): void;

  /** Check if checkout is currently mounted */
  isMounted(): boolean;

  /**
   * Complete form validation successfully - allows wallet payment to proceed.
   * Call this in response to 'form_validation' event when merchant form is valid.
   * @param wallet - The wallet type that requested validation ('apple_pay' | 'google_pay')
   */
  completeFormValidation(wallet: WalletType): void;

  /**
   * Fail form validation - prevents wallet payment from starting.
   * Call this in response to 'form_validation' event when merchant form has errors.
   * @param wallet - The wallet type that requested validation ('apple_pay' | 'google_pay')
   * @param errors - Optional array of validation errors to pass back
   */
  failFormValidation(wallet: WalletType, errors?: FormValidationError[]): void;

  /**
   * Dynamically update the checkout appearance after it has been rendered.
   * Useful for syncing the checkout theme with the merchant page (e.g. dark mode toggle).
   * @param appearance - Partial appearance options to merge into the current appearance
   */
  updateAppearance(appearance: Appearance): void;
}

// Apple Pay bridge message names — wire format shared with hosted-checkout's CheckoutMessageType enum.
// Defined here as a const so the switch in index.ts is compile-checked within this repo.
// If a message name changes in hosted-checkout, update this map and the switch together.
export const ApplePayBridgeMsg = {
  CAPABILITIES:     'SDK_CAPABILITIES',
  START:            'APPLE_PAY_START',
  VALIDATE_MERCHANT:'APPLE_PAY_VALIDATE_MERCHANT',
  MERCHANT_SESSION: 'APPLE_PAY_MERCHANT_SESSION',
  MERCHANT_ERROR:   'APPLE_PAY_MERCHANT_ERROR',
  PAYMENT_DATA:     'APPLE_PAY_PAYMENT_DATA',
  PAYMENT_RESULT:   'APPLE_PAY_PAYMENT_RESULT',
  CANCELLED:        'APPLE_PAY_CANCELLED',
  SESSION_ERROR:    'APPLE_PAY_SESSION_ERROR',
} as const;

/**
 * PaypercutCheckout static interface (callable and constructable)
 */
export interface PaypercutCheckoutStatic {
  /** Callable factory function */
  (options: PaypercutCheckoutOptions): CheckoutInstance;

  /** Constructor support */
  new (options: PaypercutCheckoutOptions): CheckoutInstance;

  /** SDK version */
  version: string;
}
